import type { ReactNode } from "react"

interface SearchResultSet {
  properties?: Array<{ id: string; name: string; slug: string; host_email: string }>
  users?: Array<{ id: string; email: string; name: string; role: string; is_active: boolean }>
  devices?: Array<{ id: string; token_hash: string; property_id: string; last_seen_at: string; created_at: string }>
  bookings?: Array<{ id: string; property_id: string; property_name: string; booking_number: string; start_date: string; end_date: string; summary: string }>
}

export function OpsGlobalSearch({
  query,
  onQueryChange,
  onSubmit,
  isSearching,
  results,
  onOpenProperty,
  onOpenDevice,
  onViewLogs,
  onCopy,
}: {
  query: string
  onQueryChange: (value: string) => void
  onSubmit: () => void
  isSearching: boolean
  results: SearchResultSet | null
  onOpenProperty: (propertyId: string) => void
  onOpenDevice: (propertyId: string, deviceId: string) => void
  onViewLogs: (propertyId: string) => void
  onCopy: (value: string) => void
}) {
  return (
    <div className="section-card">
      <h2 className="section-card__title">Global Search</h2>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
        className="ops-search-form"
      >
        <input
          className="audit-filters__input"
          style={{ flex: 1, minWidth: 280 }}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search property, host email, device id, booking number"
        />
        <button className="data-table__action" type="submit" disabled={isSearching}>
          {isSearching ? "Searching..." : "Search"}
        </button>
      </form>

      <div className="ops-search-groups">
        <SearchGroup title="Properties" emptyLabel="No properties" count={results?.properties?.length || 0}>
          {(results?.properties || []).map((item) => (
            <SearchRow
              key={item.id}
              title={item.name || item.id}
              subtitle={`${item.id} • ${item.host_email || "No host email"}`}
              onOpen={() => onOpenProperty(item.id)}
              openLabel="Open property"
              onLogs={() => onViewLogs(item.id)}
              onCopy={() => onCopy(item.id)}
            />
          ))}
        </SearchGroup>

        <SearchGroup title="Hosts / Users" emptyLabel="No users" count={results?.users?.length || 0}>
          {(results?.users || []).map((item) => (
            <SearchRow
              key={item.id}
              title={item.email}
              subtitle={`${item.name || "No name"} • ${item.role}`}
              onOpen={null}
              openLabel="Open"
              onLogs={null}
              onCopy={() => onCopy(item.id)}
            />
          ))}
        </SearchGroup>

        <SearchGroup title="Devices" emptyLabel="No devices" count={results?.devices?.length || 0}>
          {(results?.devices || []).map((item) => (
            <SearchRow
              key={item.token_hash}
              title={item.id}
              subtitle={`Property ${item.property_id} • ${item.last_seen_at || "Never seen"}`}
              onOpen={() => onOpenDevice(item.property_id, item.token_hash)}
              openLabel="Open device"
              onLogs={() => onViewLogs(item.property_id)}
              onCopy={() => onCopy(item.token_hash)}
            />
          ))}
        </SearchGroup>

        <SearchGroup title="Bookings" emptyLabel="No bookings" count={results?.bookings?.length || 0}>
          {(results?.bookings || []).map((item) => (
            <SearchRow
              key={item.id}
              title={item.booking_number || item.id}
              subtitle={`${item.property_name || item.property_id} • ${item.start_date} to ${item.end_date}`}
              onOpen={() => onOpenProperty(item.property_id)}
              openLabel="Open property"
              onLogs={() => onViewLogs(item.property_id)}
              onCopy={() => onCopy(item.id)}
            />
          ))}
        </SearchGroup>
      </div>
    </div>
  )
}

function SearchGroup({
  title,
  count,
  emptyLabel,
  children,
}: {
  title: string
  count: number
  emptyLabel: string
  children: ReactNode
}) {
  return (
    <div className="ops-search-group">
      <div className="ops-search-group__title">
        {title}
        <span className="ops-search-group__count">{count}</span>
      </div>
      <div className="ops-search-group__list">
        {count > 0 ? children : <div className="ops-search-group__empty">{emptyLabel}</div>}
      </div>
    </div>
  )
}

function SearchRow({
  title,
  subtitle,
  onOpen,
  openLabel,
  onLogs,
  onCopy,
}: {
  title: string
  subtitle: string
  onOpen: (() => void) | null
  openLabel: string
  onLogs: (() => void) | null
  onCopy: () => void
}) {
  return (
    <div className="ops-search-row">
      <div className="ops-search-row__content">
        <div className="ops-search-row__title">{title}</div>
        <div className="ops-search-row__subtitle">{subtitle}</div>
      </div>
      <div className="ops-search-row__actions">
        {onOpen && (
          <button className="data-table__action" type="button" onClick={onOpen}>
            {openLabel}
          </button>
        )}
        {onLogs && (
          <button className="data-table__action" type="button" onClick={onLogs}>
            View logs
          </button>
        )}
        <button className="data-table__action" type="button" onClick={onCopy}>
          Copy ID
        </button>
      </div>
    </div>
  )
}
